import {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useContext,
    useState
} from "react";

import { OfferingAllowances } from "@/components/usage-based-pricing-calculator/src/model/index.ts";

import { CollectionsInputs } from "./types";

export interface UbpContextValue {
    inputs: UbpContextInputs;
    step: UbpStep;
    setStep: Dispatch<SetStateAction<UbpStep>>;
    productCatalog: ProductCatalogState;
    setProductCatalog: Dispatch<SetStateAction<ProductCatalogState>>;
}

export enum UbpStep {
    collections = 0,
    orgChangePercent = 1,
    usageEstimate = 2,
    plans = 3
}

export interface UbpContextInputs {
    values: UbpContextInputValues;
    setters: UbpContextInputSetters;
}

export interface ProductCatalogState {
    loading: boolean;
    data: OfferingAllowances | null;
    error: unknown;
}

export interface UbpContextInputValues {
    collections: CollectionsInputs;
    orgChangePercent: number;
    orgUserCount: number;
    chatAdoptionPercent: number;
    chatUsersPerRulePerMonth: number;
    automationActiveRules: number;
    automationRunsPerRulePerMonth: number;
}

export interface UbpContextInputSetters {
    setCollections: Dispatch<SetStateAction<CollectionsInputs>>;
    setOrgChangePercent: Dispatch<SetStateAction<number>>;
    setOrgUserCount: Dispatch<SetStateAction<number>>;
    setChatAdoptionPercent: Dispatch<SetStateAction<number>>;
    setChatUsersPerRulePerMonth: Dispatch<SetStateAction<number>>;
    setAutomationActiveRules: Dispatch<SetStateAction<number>>;
    setAutomationRunsPerRulePerMonth: Dispatch<SetStateAction<number>>;
}

const UbpContext = createContext<UbpContextValue | undefined>(undefined);

export const useUbpContext = () => {
    const ctx = useContext(UbpContext);
    if (!ctx) {
        throw new Error("missing context provider for UbpContext");
    }
    return ctx;
};

const defaultCollections: CollectionsInputs = {
    teamwork: { selected: false, plan: "premium" },
    strategy: { selected: false, plan: "premium" },
    service: { selected: false, plan: "premium" },
    software: { selected: false, plan: "premium" }
};

const DEFAULT_ORG_CHANGE_PERCENT = 1;
const DEFAULT_ORG_USER_COUNT = 0;
const DEFAULT_CHAT_ADOPTION_PERCENT = 0;
const DEFAULT_CHAT_USERS_PER_RULE_PER_MONTH = 0;
const DEFAULT_AUTOMATION_ACTIVE_RULES = 0;
const DEFAULT_AUTOMATION_RUNS_PER_RULE_PER_MONTH = 0;
const DEFAULT_STEP = UbpStep.collections;

export type UbpContextProviderProps = PropsWithChildren;

export const UbpContextProvider = (props: UbpContextProviderProps) => {
    const [collections, setCollections] =
        useState<CollectionsInputs>(defaultCollections);
    const [orgChangePercent, setOrgChangePercent] = useState(
        DEFAULT_ORG_CHANGE_PERCENT
    );
    const [orgUserCount, setOrgUserCount] = useState(DEFAULT_ORG_USER_COUNT);
    const [chatAdoptionPercent, setChatAdoptionPercent] = useState(
        DEFAULT_CHAT_ADOPTION_PERCENT
    );
    const [chatUsersPerRulePerMonth, setChatUsersPerRulePerMonth] = useState(
        DEFAULT_CHAT_USERS_PER_RULE_PER_MONTH
    );
    const [automationActiveRules, setAutomationActiveRules] = useState(
        DEFAULT_AUTOMATION_ACTIVE_RULES
    );
    const [automationRunsPerRulePerMonth, setAutomationRunsPerRulePerMonth] =
        useState(DEFAULT_AUTOMATION_RUNS_PER_RULE_PER_MONTH);

    const [step, setStep] = useState<UbpStep>(DEFAULT_STEP);

    const [productCatalog, setProductCatalog] = useState<ProductCatalogState>({
        loading: false,
        data: null,
        error: null
    });

    return (
        <UbpContext.Provider
            value={{
                inputs: {
                    values: {
                        collections,
                        orgChangePercent,
                        orgUserCount,
                        chatAdoptionPercent,
                        chatUsersPerRulePerMonth,
                        automationActiveRules,
                        automationRunsPerRulePerMonth
                    },
                    setters: {
                        setCollections,
                        setOrgChangePercent,
                        setOrgUserCount,
                        setChatAdoptionPercent,
                        setChatUsersPerRulePerMonth,
                        setAutomationActiveRules,
                        setAutomationRunsPerRulePerMonth
                    }
                },
                step,
                setStep,
                productCatalog,
                setProductCatalog
            }}
        >
            {props.children}
        </UbpContext.Provider>
    );
};
